import { listContentLinks } from '@/api/content-link-api';
import { fetchMyObligationPriorities } from '@/api/content-api';
import { executeTool } from '@/api/tools-api';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useNavigateWithContentFlush } from '@/hooks';
import { useContentAttachmentsForContents } from '@/hooks/useContentAttachmentQueries';
import { invalidateObligationViews } from '@/hooks/useObligationQueries';
import { cn } from '@/lib/utils';
import { toContentHref } from '@/utils/content-ref';
import { getAxiosErrorDisplayMessage } from '@/utils/error-utils';
import { MESSAGE_EXTRACT_OUTCOMES_BY_KIND } from '@vivport/backend/types';
import type { MessageExtractContent, TransmittalPartySummary } from '@vivport/backend/types';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Highlighter, Loader2, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { MESSAGE_HIGHLIGHT_QUOTE_EVENT } from './message-events';
import { KIND_CLASSES, type ExtractKind } from './obligation-kind';
import { ObligationTimeline } from './obligation-timeline';

type ExtractStatus = MessageExtractContent['status'];
type ExtractOutcome = NonNullable<MessageExtractContent['outcome']>;

interface MessageExtractPanelProps {
  /** The thread's message content ids — obligations are aggregated across all of them. */
  sourceContentIds: number[];
  /** Candidate assignees — the source communication's parties. */
  parties: TransmittalPartySummary[];
}

interface ExtractToolResult {
  success: boolean;
  summary: string;
  failure: { code: string; message: string; retryable: boolean } | null;
}

const KIND_OPTIONS: readonly ExtractKind[] = ['DECISION', 'QUESTION', 'ACTION', 'FAULT', 'RISK'];
const STATUS_OPTIONS: readonly ExtractStatus[] = ['OPEN', 'RESOLVED', 'CANCELLED'];
const PRIORITY_OPTIONS: readonly number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

// Pre-fill default when the member has not set a priority: crude linear bucketing of the
// shared computedScore (1–1000, lower = more important) onto P1–P10 (≤100 → P1, >900 → P10).
function suggestedPriority(computedScore: number | null): number | null {
  if (computedScore === null) return null;
  return Math.min(10, Math.max(1, Math.ceil(computedScore / 100)));
}

function outcomeOptions(kind: ExtractKind): readonly ExtractOutcome[] {
  return MESSAGE_EXTRACT_OUTCOMES_BY_KIND[kind];
}

function isMessageExtract(item: { contentType: string }): item is MessageExtractContent {
  return item.contentType === 'MESSAGE_EXTRACT';
}

export function MessageExtractPanel({ sourceContentIds, parties }: MessageExtractPanelProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigateWithContentFlush();
  const attachmentsQuery = useContentAttachmentsForContents(sourceContentIds);
  const sourceKey = sourceContentIds.join(',');
  const [highlightedExtractId, setHighlightedExtractId] = useState<number | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  const toggleHistory = (extractId: number): void => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(extractId)) {
        next.delete(extractId);
      } else {
        next.add(extractId);
      }
      return next;
    });
  };

  const toggleHighlight = (
    extractId: number,
    quote: string | undefined,
    sourceContentId: number | undefined
  ): void => {
    const turningOff = highlightedExtractId === extractId;
    window.dispatchEvent(
      new CustomEvent(MESSAGE_HIGHLIGHT_QUOTE_EVENT, {
        detail: {
          quote: turningOff ? null : quote ?? null,
          sourceContentId: turningOff ? null : sourceContentId ?? null,
        },
      })
    );
    setHighlightedExtractId(turningOff ? null : extractId);
  };

  const rawExtracts = useMemo(
    () => attachmentsQuery.items.filter(isMessageExtract),
    [attachmentsQuery.items]
  );

  const extractIdsKey = useMemo(
    () => rawExtracts.map((e) => e.id).sort((a, b) => a - b).join(','),
    [rawExtracts]
  );

  // Non-blocking: the panel renders without this; it carries the member's personal priority
  // and the shared computedScore (payload-only — never displayed raw).
  const prioritiesQuery = useQuery({
    queryKey: ['my-obligation-priorities', sourceKey, extractIdsKey],
    queryFn: () => fetchMyObligationPriorities(rawExtracts.map((e) => e.id)),
    enabled: rawExtracts.length > 0,
  });

  const priorityByExtract = useMemo(() => {
    const map = new Map<number, { personalPriority: number | null; computedScore: number | null }>();
    for (const item of prioritiesQuery.data?.items ?? []) {
      map.set(item.extractContentId, {
        personalPriority: item.personalPriority,
        computedScore: item.computedScore,
      });
    }
    return map;
  }, [prioritiesQuery.data?.items]);

  // Mirrors the authoritative tiered ordering in backend list-my-obligations.ts:
  // personal priority asc → computedScore asc → dueAt asc nulls last → createdAt asc.
  // All ascending; LOWER = MORE IMPORTANT. Unscored items render immediately (dueAt tier)
  // and re-sort when scores arrive on the next refetch.
  const extracts = useMemo(() => {
    const tierOf = (id: number): number => {
      const entry = priorityByExtract.get(id);
      if (entry?.personalPriority != null) return 0;
      if (entry?.computedScore != null) return 1;
      return 2;
    };
    const dueMs = (dueAt: string | null): number =>
      dueAt === null ? Number.POSITIVE_INFINITY : new Date(dueAt).getTime();
    return [...rawExtracts].sort((a, b) => {
      const tierA = tierOf(a.id);
      const tierB = tierOf(b.id);
      if (tierA !== tierB) return tierA - tierB;
      if (tierA === 0) {
        const pa = priorityByExtract.get(a.id)?.personalPriority ?? 0;
        const pb = priorityByExtract.get(b.id)?.personalPriority ?? 0;
        if (pa !== pb) return pa - pb;
      }
      if (tierA === 1) {
        const sa = priorityByExtract.get(a.id)?.computedScore ?? 0;
        const sb = priorityByExtract.get(b.id)?.computedScore ?? 0;
        if (sa !== sb) return sa - sb;
      }
      if (dueMs(a.dueAt) !== dueMs(b.dueAt)) return dueMs(a.dueAt) - dueMs(b.dueAt);
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });
  }, [rawExtracts, priorityByExtract]);

  const assigneeCandidates = useMemo(() => {
    const byPartyId = new Map<number, { partyId: number; label: string }>();
    for (const party of parties) {
      if (!byPartyId.has(party.partyId)) {
        byPartyId.set(party.partyId, {
          partyId: party.partyId,
          label: party.name ? `${party.name} (${party.email})` : party.email,
        });
      }
    }
    return [...byPartyId.values()];
  }, [parties]);

  const updateLinkQueries = useQueries({
    queries: extracts.map((extract) => ({
      queryKey: ['content-links', extract.id] as const,
      queryFn: () => listContentLinks(extract.id),
      staleTime: 60 * 1000,
    })),
  });

  const invalidate = () => {
    invalidateObligationViews(queryClient);
    for (const extract of extracts) {
      queryClient.invalidateQueries({ queryKey: ['content-links', extract.id] });
      queryClient.invalidateQueries({ queryKey: ['obligation-arc', extract.id] });
    }
  };

  const handleToolResponse = (
    response: Awaited<ReturnType<typeof executeTool<unknown, ExtractToolResult>>>,
    successMessage: string
  ): void => {
    if (response.status === 'FAILED') {
      toast.error(response.error ?? 'Extract update failed.');
      return;
    }
    if (response.output && response.output.success === false) {
      toast.error(response.output.failure?.message ?? response.output.summary);
      return;
    }
    toast.success(successMessage);
    invalidate();
  };

  const setStateMutation = useMutation({
    mutationFn: (input: {
      extractContentId: number;
      kind?: ExtractKind;
      status?: ExtractStatus;
      outcome?: ExtractOutcome | null;
    }) => executeTool<typeof input, ExtractToolResult>('content.setMessageExtractState', input),
  });

  const reassignMutation = useMutation({
    mutationFn: (input: { extractContentId: number; assigneePartyId: number }) =>
      executeTool<typeof input, ExtractToolResult>('content.reassignMessageExtract', input),
  });

  const setPriorityMutation = useMutation({
    mutationFn: (input: { extractContentId: number; priority: number | null }) =>
      executeTool<
        typeof input,
        { extractContentId: number; personalPriority: number | null; summary: string }
      >('content.setMyObligationPriority', input),
  });

  const runSetPriority = (extractContentId: number, priority: number | null): void => {
    setPriorityMutation.mutate(
      { extractContentId, priority },
      {
        onSuccess: (response) => {
          if (response.status === 'FAILED') {
            toast.error(response.error ?? 'Priority update failed.');
            return;
          }
          toast.success(priority === null ? 'Priority cleared.' : `Priority set to P${priority}.`);
          queryClient.invalidateQueries({
            queryKey: ['my-obligation-priorities', sourceKey],
          });
          invalidate();
        },
        onError: (error) =>
          toast.error(getAxiosErrorDisplayMessage(error, 'Priority update failed.')),
      }
    );
  };

  const runSetState = (
    input: Parameters<typeof setStateMutation.mutate>[0],
    successMessage: string
  ): void => {
    setStateMutation.mutate(input, {
      onSuccess: (response) => handleToolResponse(response, successMessage),
      onError: (error) => toast.error(getAxiosErrorDisplayMessage(error, 'Extract update failed.')),
    });
  };

  const runReassign = (extractContentId: number, assigneePartyId: number): void => {
    reassignMutation.mutate(
      { extractContentId, assigneePartyId },
      {
        onSuccess: (response) => handleToolResponse(response, 'Extract reassigned.'),
        onError: (error) => toast.error(getAxiosErrorDisplayMessage(error, 'Reassignment failed.')),
      }
    );
  };

  const detachMutation = useMutation({
    mutationFn: (input: { extractContentId: number; messageContentId: number }) =>
      executeTool<typeof input, ExtractToolResult>('content.detachObligationUpdate', input),
  });

  const runDetach = (extractContentId: number, messageContentId: number, code: string): void => {
    if (
      !window.confirm(
        `Detach ${code} from this obligation? Its changes will be reverted unless later messages moved the obligation again.`
      )
    ) {
      return;
    }
    detachMutation.mutate(
      { extractContentId, messageContentId },
      {
        onSuccess: (response) => handleToolResponse(response, 'Message detached.'),
        onError: (error) => toast.error(getAxiosErrorDisplayMessage(error, 'Detach failed.')),
      }
    );
  };

  if (extracts.length === 0) {
    return null;
  }

  const isMutating =
    setStateMutation.isPending || reassignMutation.isPending || setPriorityMutation.isPending;

  return (
    <div data-testid="message-extract-panel" className="flex flex-col gap-2 pt-4">
      <h3 className="flex items-center gap-2 font-medium">
        Obligations
        {isMutating ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : null}
      </h3>
      <ul className="divide-y rounded-lg border">
        {extracts.map((extract, index) => {
          const incomingUpdates = (updateLinkQueries[index]?.data?.incoming ?? []).filter(
            (neighbor) => neighbor.contentType === 'MESSAGE'
          );
          const assigneeOptions = extract.assignee
            ? [
                ...assigneeCandidates.filter((c) => c.partyId !== extract.assignee?.partyId),
                {
                  partyId: extract.assignee.partyId,
                  label: extract.assignee.name
                    ? `${extract.assignee.name} (${extract.assignee.email})`
                    : extract.assignee.email,
                },
              ]
            : assigneeCandidates;

          const priorityEntry = priorityByExtract.get(extract.id);
          const personalPriority = priorityEntry?.personalPriority ?? null;
          const suggested = suggestedPriority(priorityEntry?.computedScore ?? null);
          const isUnscored =
            prioritiesQuery.data !== undefined && priorityEntry?.computedScore == null;

          return (
            <li key={extract.id} className="space-y-2 p-3">
              <div className="flex flex-wrap items-center gap-2">
                <Select
                  value={extract.kind}
                  onValueChange={(kind) =>
                    runSetState(
                      { extractContentId: extract.id, kind: kind as ExtractKind },
                      `Kind corrected to ${kind}.`
                    )
                  }
                  disabled={isMutating}
                >
                  <SelectTrigger
                    aria-label="Extract kind"
                    className={cn('h-7 w-auto gap-1 rounded-full border px-2.5 text-xs font-medium', KIND_CLASSES[extract.kind])}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {KIND_OPTIONS.map((kind) => (
                      <SelectItem key={kind} value={kind}>
                        {kind}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={extract.status}
                  onValueChange={(status) =>
                    runSetState(
                      { extractContentId: extract.id, status: status as ExtractStatus },
                      `Status set to ${status}.`
                    )
                  }
                  disabled={isMutating}
                >
                  <SelectTrigger aria-label="Extract status" className="h-7 w-auto gap-1 px-2.5 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={personalPriority !== null ? String(personalPriority) : ''}
                  onValueChange={(value) =>
                    runSetPriority(extract.id, value === 'AUTO' ? null : Number(value))
                  }
                  disabled={isMutating}
                >
                  <SelectTrigger
                    aria-label="My priority"
                    title="My priority (1 = most important)"
                    className="h-7 w-auto gap-1 px-2.5 text-xs"
                  >
                    <SelectValue placeholder={suggested !== null ? `Auto (P${suggested})` : 'Auto'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AUTO">Auto</SelectItem>
                    {PRIORITY_OPTIONS.map((priority) => (
                      <SelectItem key={priority} value={String(priority)}>
                        P{priority}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {isUnscored ? (
                  <span className="text-xs italic text-muted-foreground">scoring…</span>
                ) : null}

                {extract.sourceHighlight ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      'h-7 w-7 border border-input',
                      highlightedExtractId === extract.id
                        ? 'bg-highlight text-highlight-foreground hover:bg-highlight-strong/50'
                        : 'bg-transparent text-muted-foreground'
                    )}
                    aria-label="Toggle source-text highlight in message"
                    aria-pressed={highlightedExtractId === extract.id}
                    title="Toggle source-text highlight in message"
                    onClick={() =>
                      toggleHighlight(
                        extract.id,
                        extract.sourceHighlight?.quote,
                        extract.sourceHighlight?.sourceContentId
                      )
                    }
                  >
                    <Highlighter className="h-4 w-4" />
                  </Button>
                ) : null}

                {outcomeOptions(extract.kind).length > 0 ? (
                  <Select
                    value={extract.outcome ?? 'NONE'}
                    onValueChange={(outcome) =>
                      runSetState(
                        {
                          extractContentId: extract.id,
                          outcome: outcome === 'NONE' ? null : (outcome as ExtractOutcome),
                        },
                        outcome === 'NONE' ? 'Outcome cleared.' : `Outcome set to ${outcome}.`
                      )
                    }
                    disabled={isMutating}
                  >
                    <SelectTrigger aria-label="Extract outcome" className="h-7 w-auto gap-1 px-2.5 text-xs">
                      <SelectValue placeholder="Outcome" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NONE">No outcome</SelectItem>
                      {outcomeOptions(extract.kind).map((outcome) => (
                        <SelectItem key={outcome} value={outcome}>
                          {outcome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : null}

                {extract.dueAt ? (
                  <span className="text-xs text-muted-foreground">
                    Due {new Date(extract.dueAt).toLocaleDateString()}
                  </span>
                ) : null}

                <span className="ml-auto flex items-center gap-1 text-xs text-muted-foreground">
                  <Select
                    value={extract.assignee ? String(extract.assignee.partyId) : ''}
                    onValueChange={(partyId) => runReassign(extract.id, Number(partyId))}
                    disabled={isMutating || assigneeOptions.length === 0}
                  >
                    <SelectTrigger aria-label="Extract assignee" className="h-7 w-auto gap-1 px-2.5 text-xs">
                      <SelectValue placeholder="Unassigned" />
                    </SelectTrigger>
                    <SelectContent>
                      {assigneeOptions.map((candidate) => (
                        <SelectItem key={candidate.partyId} value={String(candidate.partyId)}>
                          {candidate.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </span>
              </div>

              <div className="min-w-0 cursor-default">
                {extract.title ? (
                  <h3 className="truncate text-sm font-medium text-foreground">{extract.title}</h3>
                ) : null}
                {extract.description ? (
                  <p className="line-clamp-2 text-sm text-muted-foreground">{extract.description}</p>
                ) : null}
              </div>

              {extract.sourceHighlight ? (
                <blockquote className="border-l-2 border-muted-foreground/30 pl-2 text-xs italic text-muted-foreground">
                  “{extract.sourceHighlight.quote}”
                </blockquote>
              ) : null}

              {incomingUpdates.length > 0 ? (
                <p className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
                  Updated by
                  {incomingUpdates.map((neighbor) => {
                    const label = neighbor.code || `#${neighbor.id}`;
                    return (
                      <span key={neighbor.id} className="inline-flex items-center gap-0.5">
                        <button
                          type="button"
                          className="text-primary underline-offset-2 hover:underline"
                          onClick={() => navigate(toContentHref(neighbor.code || String(neighbor.id)))}
                        >
                          {label}
                        </button>
                        <button
                          type="button"
                          aria-label={`Detach ${label} from this obligation`}
                          title="Not about this obligation"
                          className="rounded p-0.5 text-muted-foreground hover:text-destructive"
                          disabled={detachMutation.isPending}
                          onClick={() => runDetach(extract.id, neighbor.id, label)}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    );
                  })}
                </p>
              ) : null}

              <div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 gap-1 px-1 text-xs text-muted-foreground"
                  aria-expanded={expandedIds.has(extract.id)}
                  onClick={() => toggleHistory(extract.id)}
                >
                  {expandedIds.has(extract.id) ? (
                    <ChevronDown className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5" />
                  )}
                  History
                </Button>
                {expandedIds.has(extract.id) ? (
                  <ObligationTimeline extractId={extract.id} enabled={expandedIds.has(extract.id)} />
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
